'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import {
  TOP_NAV_DATA_GA,
  TOP_NAV_DATA_TEST,
} from '@/app/components/layouts/top-navigation/tracking';
import { TOP_NAV_MENU_LABEL } from '@/app/constants/top-navigation';
import { NAV_INTEREST } from '@/app/contents/top-navigation';
import type { NavItemType } from '@/app/types/top-navigation';
import type { VehicleType } from '@/app/types/vehicle-type';
import { createDataGA } from '@/app/utils/createDataGA';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { DesktopMenu } from '@/components/layouts/top-navigation/desktop/menu';
import { DesktopSubMenu } from '@/components/layouts/top-navigation/desktop/sub-menu';
import { MobileMenu } from '@/components/layouts/top-navigation/mobile/menu';
import type { TopNavigationVariantType } from '@/components/layouts/top-navigation/types';
import { Button } from '@/components/shared/buttons/button';
import { Link } from '@/components/shared/element/link';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import {
  clearBodyLockScroll,
  enableBodyLockScroll,
} from '@/components/shared/utility/lock-scroll';
import { config, css } from '@/stitches.config';

const TopNavigationInnerStyled = css({
  position: 'relative',
  zIndex: '$header',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '$size-14',
  gap: '$4',
  variants: {
    variant: {
      filled: {
        color: '#555555', // tailwind gray-700
      },
      transparent: {
        color: '$white',
      },
    },
  },
  defaultVariants: {
    variant: 'filled',
  },
});

interface TopNavigationProps {
  Logo: ReactNode;
  items: NavItemType[];
  vehicleType: VehicleType;
  variant?: TopNavigationVariantType;
}

export const TopNavigation = ({
  Logo,
  items,
  vehicleType,
  variant,
}: TopNavigationProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isDesktop = useMediaQuery(config.media.xl);
  const [shouldRenderMenu, setShouldRenderMenu] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeItem, setActiveItem] = useState<NavItemType | undefined>(
    undefined
  );

  const isDesktopMenuOpen = !!isDesktop && isOpen;
  const adjustedVariant = isDesktopMenuOpen ? 'filled' : variant;

  const openMenu = () => {
    if (isOpen && !isDesktop) return;

    setShouldRenderMenu(true);
    setIsOpen(true);
    if (!isDesktop) {
      enableBodyLockScroll({
        onlyOverflow: true,
      });
      setActiveItem(undefined);
    }
  };
  const closeMenu = () => {
    setIsOpen(false);
    clearBodyLockScroll();
  };
  const onMouseLeaveMenu = (e) => {
    if (!isDesktopMenuOpen || !e.relatedTarget) return;

    if (!e.relatedTarget.tagName) {
      closeMenu();
      return;
    }

    if (e.relatedTarget.tagName.toLowerCase() !== 'aside') {
      closeMenu();
    }
  };

  // Note: Always reset state when page and layout (desktop <-> mobile) changed
  useEffect(() => {
    setShouldRenderMenu(false);
    setActiveItem(undefined);
    closeMenu();
  }, [pathname, isDesktop]);

  useEffect(() => {
    if (!isOpen) return;

    closeMenu();
    if (!isDesktop) {
      setActiveItem(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <Container
        aria-label="Navigation"
        as="nav"
        className={TopNavigationInnerStyled({
          variant: adjustedVariant,
        })}
        data-test={TOP_NAV_DATA_TEST.navigation}
      >
        {!!Logo && (
          <Link
            href="/"
            test={{
              dataTest: TOP_NAV_DATA_TEST.logo,
            }}
            tracking={{
              dataGA: createDataGA({
                eventName: TOP_NAV_DATA_GA.topNavigation,
                text: TOP_NAV_DATA_GA.logo,
                url: '/',
                click: true,
              }),
            }}
            css={{
              aspectRatio: '8 / 3',
              display: 'flex',
              alignItems: 'center',
              color: adjustedVariant === 'filled' ? '$primary' : '$white',
              '& svg': {
                width: vehicleType === 'lcv' ? '$size-24' : 132,
              },
            }}
            onMouseEnter={closeMenu}
          >
            {Logo}
          </Link>
        )}

        <Box
          role="menubar"
          display="flex"
          alignItems="center"
          columnGap="4"
          height="full"
          onMouseLeave={onMouseLeaveMenu}
        >
          <DesktopMenu
            items={items}
            activeItem={activeItem}
            isOpen={isDesktopMenuOpen}
            variant={adjustedVariant}
            onOpen={openMenu}
            onSetActiveItem={setActiveItem}
            onClose={closeMenu}
          />

          <Button
            size="md"
            href={NAV_INTEREST.href}
            aria-label={NAV_INTEREST.label}
            test={{
              dataTest: TOP_NAV_DATA_TEST.interestButton,
            }}
            tracking={{
              dataGA: createDataGA({
                eventName: TOP_NAV_DATA_GA.topNavigation,
                text: NAV_INTEREST.label,
                url: NAV_INTEREST.href,
                click: true,
              }),
            }}
            variant={
              adjustedVariant === 'transparent' ? 'button-white' : 'button-red'
            }
            border={adjustedVariant === 'transparent'}
            label={NAV_INTEREST.label}
            css={{
              minWidth: 0,
              py: '$space-2',
              px: '$space-3',
              whiteSpace: 'nowrap',
              '&::before': {
                borderColor: '$white',
              },
            }}
            onFocus={closeMenu}
            onMouseEnter={closeMenu}
          />

          <Button
            size="md"
            border
            variant={
              adjustedVariant === 'transparent'
                ? 'button-white'
                : 'button-black'
            }
            onClick={openMenu}
            test={{
              dataTest: TOP_NAV_DATA_TEST.menuButton,
            }}
            tracking={{
              dataGA: createDataGA({
                eventName: TOP_NAV_DATA_GA.topNavigation,
                text: TOP_NAV_MENU_LABEL,
                click: true,
              }),
            }}
            aria-label={TOP_NAV_MENU_LABEL}
            label={TOP_NAV_MENU_LABEL}
            css={{
              minWidth: 0,
              py: '$space-2',
              px: '$space-3',
              '@xl': {
                display: 'none',
              },
            }}
          />
        </Box>
      </Container>
      {!!shouldRenderMenu &&
        (isDesktop ? (
          <DesktopSubMenu
            isOpen={isDesktopMenuOpen}
            activeItem={activeItem}
            onClose={closeMenu}
          />
        ) : (
          <MobileMenu
            Logo={Logo}
            isOpen={isOpen}
            activeItem={activeItem}
            items={items}
            vehicleType={vehicleType}
            onSetActiveItem={setActiveItem}
            onClose={closeMenu}
          />
        ))}
    </>
  );
};
